import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, User, Building, IndianRupee, Send, Phone, MapPin } from 'lucide-react';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    projectType: '',
    budget: '',
    message: ''
  });

  const [csrfToken, setCsrfToken] = useState('');
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    // Simple CSRF token generation for PHP endpoint (double-submit cookie pattern)
    const token = Math.random().toString(36).substring(2) + Date.now().toString(36);
    setCsrfToken(token);
    // Store token in cookie so PHP can validate (double-submit cookie pattern)
    document.cookie = `csrf_token=${token}; path=/; SameSite=Lax`;
  }, []);


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const payload = new URLSearchParams({
        ...formData,
        csrf_token: csrfToken,
      });

      const response = await fetch('/form.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: payload,
      });

      const result = await response.json();

      if (result.success) {
        setFormStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.'
        });
        setFormData({
          name: '',
          company: '',
          email: '',
          projectType: '',
          budget: '',
          message: ''
        });
      } else {
        setFormStatus({
          type: 'error',
          message: result.message || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Failed to send message. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-section" id="contact">
      <div className="contact-container">
        <div className="contact-header">
          <h2 className="section-title">Let's Work Together</h2>
          <p className="section-description">
            Ready to start your white-label Laravel project? Get in touch today.
          </p>
        </div>
        
        <div className="contact-content">
          <div className="contact-info">
            <h3 className="contact-info-title">Contact Information</h3>
            <p className="contact-info-text">
              Reach out for a free discussion, estimate, or any questions about white-label Laravel development.
            </p>
            
            <div className="contact-details">
              <div className="contact-detail-item">
                <Mail size={24} />
                <div>
                  <strong>Email</strong>
                  <p>contact@whitelabeldevs.com</p>
                </div>
              </div>
              
              <div className="contact-detail-item">
                <Phone size={24} />
                <div>
                  <strong>WhatsApp / Phone</strong>
                  <p>+91 8766346052</p>
                </div>
              </div>
              
              <div className="contact-detail-item">
                <MapPin size={24} />
                <div>
                  <strong>Location</strong>
                  <p>2 Maa Pitambara Complex</p>
                  <p>Dwarika Green Phase 2</p>
                  <p>Agra Bamroli Road, UP 282009</p>
                  <p className="location-note">Available for local in-person meetings</p>
                </div>
              </div>
            </div>
            
            <div className="trust-line">
              <strong>White-label & NDA-friendly</strong>
              <p>Your project details stay confidential</p>
            </div>
          </div>
          
          <form className="contact-form" onSubmit={handleSubmit} action="/form.php" method="POST">
            <input type="hidden" name="csrf_token" value={csrfToken} />
            <div className="form-group">
              <label htmlFor="name">
                <User size={18} />
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your full name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="company">
                <Building size={18} />
                Company
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your company name (optional)"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">
                <Mail size={18} />
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your@email.com"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="projectType">
                <MessageSquare size={18} />
                Project Type *
              </label>
              <select
                id="projectType"
                name="projectType"
                value={formData.projectType}
                onChange={handleChange}
                required
              >
                <option value="">Select project type</option>
                <option value="white-label">White-label Laravel Development</option>
                <option value="custom-app">Custom Web App / CRM / SaaS</option>
                <option value="api">API Development & Integration</option>
                <option value="bug-fix">Bug Fixing / Rescue Project</option>
                <option value="optimization">Performance & Security</option>
                <option value="monthly-partner">Monthly Partnership</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="budget">
                <IndianRupee size={18} />
                Budget Range
              </label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
              >
                <option value="">Select budget range</option>
                <option value="10k-25k">₹10,000 - ₹25,000</option>
                <option value="25k-50k">₹25,000 - ₹50,000</option>
                <option value="50k-1lakh">₹50,000 - ₹1,00,000</option>
                <option value="1-2.5lakh">₹1,00,000 - ₹2,50,000</option>
                <option value="2.5lakh-plus">₹2,50,000+</option>
                <option value="monthly">Monthly Partnership</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="message">
                <MessageSquare size={18} />
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="5"
                placeholder="Tell us about your project requirements..."
              ></textarea>
            </div>
            
            {formStatus.message && (
              <div className={`form-status ${formStatus.type}`}>
                {formStatus.message}
              </div>
            )}
            
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              <Send size={20} />
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;